// Impression d'un document (PDF ou image) depuis n'importe quel espace, sur toutes les plateformes :
// - ordinateur (navigateur) : fenêtre d'impression du navigateur, sans quitter la page ;
// - téléphone (navigateur) : le document s'ouvre dans un onglet, imprimable depuis le menu ;
// - applications Android, Windows/Mac et iOS : le document s'ouvre dans la visionneuse du
//   système, qui propose l'impression.
// Le module de téléchargement (Tauri, Capacitor) n'est chargé qu'au moment d'imprimer.
const telechargeur = () => import("./downloadFile");

const PLATFORM = process.env.REACT_APP_PLATFORM || "web";

const estMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
// Capacitor (iOS, Android) expose un objet global ; inutile d'importer le module ici.
const plateformeCapacitor = () => (typeof window !== "undefined" && window.Capacitor?.getPlatform?.()) || "web";
const estApplication = () => PLATFORM !== "web" || plateformeCapacitor() !== "web";

async function chargerBlob(url, authenticated) {
  const { isAuthenticatedDownloadUrl } = await telechargeur();
  const token = authenticated && isAuthenticatedDownloadUrl(url) ? localStorage.getItem("access_token") : null;
  const reponse = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!reponse.ok) throw new Error(`http_error ${reponse.status}`);
  return reponse.blob();
}

function imprimerDansCadre(objetUrl) {
  let cadre = document.getElementById("ql-print-frame");
  if (!cadre) {
    cadre = document.createElement("iframe");
    cadre.id = "ql-print-frame";
    cadre.title = "Impression";
    cadre.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:0;";
    document.body.appendChild(cadre);
  }
  cadre.onload = () => {
    setTimeout(() => {
      try { cadre.contentWindow.focus(); cadre.contentWindow.print(); }
      catch { window.open(objetUrl, "_blank"); }
      setTimeout(() => URL.revokeObjectURL(objetUrl), 60000);
    }, 300);
  };
  cadre.src = objetUrl;
}

/**
 * Imprime un document. `data` : Blob déjà chargé ; sinon `url` (adresse complète ou relative au site).
 * Renvoie une promesse rejetée si le document est introuvable.
 */
export async function imprimerDocument({ url, data = null, filename = "document.pdf", mimeType = "application/pdf", authenticated = false }) {
  if (estApplication()) {
    // Dans les applications, la visionneuse du système se charge de l'impression.
    const { default: downloadFile } = await telechargeur();
    return downloadFile({ url: url || `/generated/${filename}`, filename, mimeType, authenticated, openAfterDownload: true, data });
  }
  // Sur téléphone, l'onglet doit s'ouvrir tout de suite (sinon il est bloqué) puis recevoir le document.
  const onglet = estMobile() ? window.open("", "_blank") : null;
  let blob = data;
  try {
    if (!blob) {
      const { normalizeDownloadUrl } = await telechargeur();
      blob = await chargerBlob(normalizeDownloadUrl(url), authenticated);
    }
  } catch (erreur) {
    // Fichier d'un autre domaine non lisible : on l'ouvre tel quel, l'impression se fait depuis l'onglet.
    if (onglet) onglet.location.href = url; else window.open(url, "_blank");
    return { ouvert: true };
  }
  const objetUrl = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: mimeType }));
  if (onglet) {
    onglet.location.href = objetUrl;
    setTimeout(() => URL.revokeObjectURL(objetUrl), 60000);
    return { ouvert: true };
  }
  imprimerDansCadre(objetUrl);
  return { imprime: true };
}

export default imprimerDocument;
